import type { MatrixParams, MatrixResponse, MatrixSummaryFull } from '@/api/schema'
import type { MatrixOfDestiny } from '@/api/services/numer'
import type { SessionStore } from '@/app/state/session'
import { api } from '@/app/utils/api'
import { createContext } from 'preact'
import { useContext } from 'preact/hooks'
import { create, type StoreApi, type UseBoundStore } from 'zustand'

type MatrixState = {
  loading: boolean
  matrix: MatrixOfDestiny | null
  summary: MatrixSummaryFull | null
  summaryFull: MatrixSummaryFull | null
  cacheKey: string | null
  reset(): void
  fetch(): Promise<void>
}

export type MatrixStore = UseBoundStore<StoreApi<MatrixState>>

export const MatrixStoreContext = createContext<MatrixStore | null>(null)

export function useMatrix<T>(selector: (state: MatrixState) => T) {
  const store = useContext(MatrixStoreContext)

  if (!store) {
    throw new Error('MatrixProvider is required')
  }

  return store(selector)
}

export const createMatrixStore = (sessionStore: SessionStore) => (
  create<MatrixState>((set, get) => ({
    loading: true,
    matrix: null,
    summary: null,
    summaryFull: null,
    cacheKey: null,

    reset() {
      set({
        loading: true,
        matrix: null,
        summary: null,
        summaryFull: null,
        cacheKey: null,
      })
    },

    async fetch() {
      const state = get()
      const session = sessionStore.getState()

      const birthDate = session.profile?.birthDate

      if (!birthDate) {
        throw new Error('Заполните профиль')
      }

      if (state.cacheKey === birthDate && state.matrix && state.summary) {
        return
      }

      set({
        loading: true,
        matrix: null,
        summary: null,
        summaryFull: null,
      })

      const baseResp = await api.post<MatrixResponse>(
        '/matrix',
        {
          birthDate,
        } satisfies MatrixParams,
      )

      set({
        matrix: baseResp.data.matrix,
        summary: baseResp.data.summary,
        cacheKey: birthDate,
        loading: false,
      })
    },
  }))
)
